import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { UpdatePriorityEnum } from 'src/app/Enums/UpdatePriorityEnum';
import { UpdateStatusEnum } from 'src/app/Enums/UpdateStatusEnum';
import { CreatePdvUpdateModel } from 'src/app/models/Pdv/CreatePdvUpdate.model';
import { PdvUpdateModel } from 'src/app/models/Pdv/PdvUpdate.model';
import { AdministrativeService } from 'src/app/services/administrative/administrative.service';
import { AlertMessageService } from 'src/app/services/alert-message/alert-message.service';
import { LoadingIndicatorComponent } from 'src/app/components/loading-indicator/loading-indicator.component';
import { TranslateService } from '@ngx-translate/core';
import { FlavorModel } from 'src/app/models/Pdv/Flavor.model';
import { FlavorsManagementComponent } from '../flavors-management/flavors-management.component';

@Component({
  selector: 'app-updates-management',
  templateUrl: './updates-management.component.html',
  styleUrls: ['./updates-management.component.scss']
})
export class UpdatesManagementComponent implements OnInit {
  @ViewChild('flavorsComp') flavorsComponent!: FlavorsManagementComponent;

  UpdatePriorityEnum = UpdatePriorityEnum;
  public isUpdaterManager: boolean = true;

  flavors: FlavorModel[] = [];

  priorities = [
    { label: 'admin.update-management.priority-options.1', value: UpdatePriorityEnum.Mandatory },
    { label: 'admin.update-management.priority-options.2', value: UpdatePriorityEnum.Optional },
    { label: 'admin.update-management.priority-options.3', value: UpdatePriorityEnum.Deadline }
  ];

  statuses = [
    { label: 'admin.update-management.status-options.1', value: UpdateStatusEnum.Pilot },
    { label: 'admin.update-management.status-options.2', value: UpdateStatusEnum.Production }
  ];

  displayedColumns = [
    'flavor', 'latestPdvVersion', 'versionCode', 'downloadUrl',
    'updatePriority', 'deadline', 'releaseNotes', 'updateInstructions',
    'updateStatus', 'createdAt', 'actions'
  ];

  selectedFlavor: FlavorModel | null = null;
  selectedPriority: UpdatePriorityEnum | null = null;
  selectedStatus: UpdateStatusEnum | null = null;

  version = '';
  versionCode: number | null = null;
  releaseNotes: string = '';

  deadline: Date | null = new Date();
  minDeadlineDate: Date = new Date();

  selectedFile: File | null = null;
  downloadUrl: string | null = null;

  pdvUpdates: PdvUpdateModel[] = [];

  private priorityLabels = new Map<UpdatePriorityEnum, string>([
    [UpdatePriorityEnum.Mandatory, 'admin.update-management.priority-options.1'],
    [UpdatePriorityEnum.Optional, 'admin.update-management.priority-options.2'],
    [UpdatePriorityEnum.Deadline, 'admin.update-management.priority-options.3']
  ]);

  private statusLabels = new Map<UpdateStatusEnum, string>([
    [UpdateStatusEnum.Pilot, 'admin.update-management.status-options.1'],
    [UpdateStatusEnum.Production, 'admin.update-management.status-options.2'],
    [UpdateStatusEnum.Inactive, 'admin.update-management.status-options.3'],
  ]);

  constructor(
    private adminService: AdministrativeService,
    private alertMessage: AlertMessageService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  public async togglePage(): Promise<void> {
    await (this.isUpdaterManager
      ? this.flavorsComponent?.refresh?.()
      : this.refresh()
    );
    this.isUpdaterManager = !this.isUpdaterManager;
  }

  public async refresh(): Promise<void> {
    await this.loadFlavors();
    await this.loadUpdates();
    this.resetForm();
  }

  onFlavorChange(flavor: FlavorModel) {
    if (!flavor) return;
    if (this.selectedFlavor?.id !== flavor.id) this.resetForm();
    this.selectedFlavor = flavor;
  }

  get requiresFile(): boolean {
    return this.selectedFlavor?.requiresFile ?? false;
  }

  get acceptedExtensions(): string | null {
    if (!this.selectedFlavor?.requiresFile) return null;
    return this.selectedFlavor.fileAccept ?? null;
  }

  get requiresVersionCode(): boolean {
    return this.selectedFlavor?.requiresVersionCode ?? false;
  }

  getPriorityLabel(priority: UpdatePriorityEnum): string {
    const key = this.priorityLabels.get(priority);
    return key ? this.translateService.instant(key) : '—';
  }

  getStatusLabel(status: UpdateStatusEnum): string {
    const key = this.statusLabels.get(status);
    return key ? this.translateService.instant(key) : '—';
  }

  formatValue(value: any): string {
    return value ? String(value) : '—';
  }

  private async loadFlavors(): Promise<void> {
    try {
      const response = await this.adminService.getActiveFlavors();
      this.flavors = response;
    } catch (error) {
      console.error('Erro ao carregar flavors:', error);
      this.alertMessage.alertErrorMessage(this.t('admin.update-management.errors.load-updates'));
      this.flavors = [];
    }
  }

  private async loadUpdates(): Promise<void> {
    try {
      this.pdvUpdates = await this.adminService.getPdvUpdates();
    } catch {
      this.alertMessage.alertErrorMessage(this.t('admin.update-management.errors.load-updates'));
      this.pdvUpdates = [];
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const maxSize = 157_286_400; // 150 MB
    if (file.size > maxSize) {
      this.alertMessage.alertWarningMessage(this.t('admin.update-management.errors.file-too-large'));
      input.value = '';
      this.selectedFile = null;
      return;
    }

    const accepted = this.acceptedExtensions;
    if (accepted && !file.name.toLowerCase().endsWith(accepted)) {
      this.alertMessage.alertWarningMessage(this.t('admin.update-management.errors.invalid-file-format', { expected: accepted }));
      input.value = '';
      this.selectedFile = null;
      return;
    }

    this.tryExtractVersionFromFilename(file.name);
    this.selectedFile = file;
  }


  private async uploadFileIfNeeded(): Promise<string | null> {
    if (!this.requiresFile || !this.selectedFile) {
      return this.downloadUrl ?? null;
    }

    const resp = await this.adminService.uploadPdvUpdateFile(this.selectedFile);
    return resp?.downloadUrl ?? null;
  }


  private validateCreateForm(): boolean {
    if (!this.selectedFlavor) return this.warn(this.t('admin.update-management.errors.select-flavor'));
    if (this.selectedPriority === null) return this.warn(this.t('admin.update-management.errors.select-priority'));
    if (this.requiresFile && !this.selectedFile) return this.warn(this.t('admin.update-management.errors.file-required'));
    if (!this.version?.trim()) return this.warn(this.t('admin.update-management.errors.input-version'));
    if (this.requiresVersionCode && this.versionCode == null) return this.warn(this.t('admin.update-management.errors.input-version-code'));
    if (!this.releaseNotes?.trim()) return this.warn(this.t('admin.update-management.errors.input-release-notes'));
    if (this.selectedStatus === null) return this.warn(this.t('admin.update-management.errors.select-status'));
    return true;
  }

  private t(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  private warn(msg: string): false {
    this.alertMessage.alertWarningMessage(msg);
    return false;
  }

  async submitUpdate(): Promise<void> {
    if (!this.validateCreateForm()) return;
    if (!this.isVersionValidForStatus(this.version, this.versionCode, this.selectedFlavor!, this.selectedStatus!)) {
      this.warn(this.t('admin.update-management.status.invalid-version-order'));
      return;
    }
    if (!this.canSubmitVersion(this.version, this.versionCode, this.selectedFlavor!, this.selectedPriority!, this.selectedStatus!, this.releaseNotes, this.deadline)) {
      this.warn(this.t('admin.update-management.status.same-version'));
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.data = { message: this.t('admin.update-management.create.uploading') };

    const loadingDialog = this.dialog.open(LoadingIndicatorComponent, dialogConfig);

    try {
      const uploadedUrl = await this.uploadFileIfNeeded();

      if (this.requiresFile && !uploadedUrl) {
        loadingDialog.componentInstance.failed = true;
        loadingDialog.componentInstance.message = this.t('admin.update-management.status.failed-upload');
        setTimeout(() => loadingDialog.close(), 1000);
        return;
      }

      this.downloadUrl = uploadedUrl ?? this.downloadUrl;

      const ok = await this.adminService.createPdvUpdate(this.buildCreatePayload());

      if (ok) {
        loadingDialog.componentInstance.succeeded = true;
        loadingDialog.componentInstance.message = this.t('admin.update-management.status.success-create');

        await this.loadUpdates();
        this.resetForm();

      } else {
        loadingDialog.componentInstance.failed = true;
        loadingDialog.componentInstance.message = this.t('admin.update-management.status.failed-create');
      }

    } catch {
      loadingDialog.componentInstance.failed = true;
      loadingDialog.componentInstance.message = this.t('admin.update-management.status.unexpected-error');
    }

    setTimeout(() => loadingDialog.close(), 1000);
  }

  private isVersionValidForStatus(
    version: string,
    versionCode: number | null,
    flavor: FlavorModel,
    updateStatus: UpdateStatusEnum
  ): boolean {

    const updatesForFlavor = this.pdvUpdates
      .filter(u => u.flavor === flavor.name && u.updateStatus === updateStatus);

    if (updatesForFlavor.length === 0) return true;

    const lastUpdate = updatesForFlavor
      .sort((a, b) => {
        const versionCompare = b.latestPdvVersion.localeCompare(a.latestPdvVersion);
        if (versionCompare !== 0) return versionCompare;

        if (flavor.requiresVersionCode) {
          const bCode = b.versionCode ?? 0;
          const aCode = a.versionCode ?? 0;
          if (bCode !== aCode) return bCode - aCode;
        }

        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })[0];

    const compare = version.localeCompare(lastUpdate.latestPdvVersion);
    if (compare < 0) return false;

    if (flavor.requiresVersionCode && compare === 0) {
      const lastCode = lastUpdate.versionCode ?? 0;
      const newCode = versionCode ?? 0;

      if (newCode < lastCode) return false;
    }

    return true;
  }

  private canSubmitVersion(
    version: string,
    versionCode: number | null,
    flavor: FlavorModel,
    updatePriority: UpdatePriorityEnum,
    updateStatus: UpdateStatusEnum,
    releaseNotes: string | null,
    deadline?: Date | null,
  ): boolean {

    const updatesForFlavor = this.pdvUpdates
      .filter(u => u.flavor === flavor.name);

    if (updatesForFlavor.length === 0) return true;
    
    const lastUpdate = updatesForFlavor
      .sort((a, b) => {
        const versionCompare = b.latestPdvVersion.localeCompare(a.latestPdvVersion);
        if (versionCompare !== 0) return versionCompare;

        if (flavor.requiresVersionCode) {
          const bCode = b.versionCode ?? 0;
          const aCode = a.versionCode ?? 0;
          if (bCode !== aCode) return bCode - aCode;
        }

        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })[0];


    if (lastUpdate.latestPdvVersion !== version) return true;
    if (
      flavor.requiresVersionCode &&
      (lastUpdate.versionCode ?? 0) !== (versionCode ?? 0)
    ) {
      return true;
    }

    const samePriority = lastUpdate.updatePriority === updatePriority;
    const sameStatus   = lastUpdate.updateStatus === updateStatus;

    const sameDeadline =
      updatePriority !== UpdatePriorityEnum.Deadline ||
      (
        deadline &&
        lastUpdate.deadline &&
        new Date(lastUpdate.deadline).getTime() === new Date(deadline).getTime()
      );

    if (samePriority && sameStatus && sameDeadline) {
      const currentNotes = (releaseNotes ?? '').trim();
      const lastNotes    = (lastUpdate.releaseNotes ?? '').trim();

      const hasNotes     = currentNotes.length > 0;
      const notesChanged = currentNotes !== lastNotes;

      return hasNotes && notesChanged;
    }

    return true;
  }

  private buildCreatePayload(): CreatePdvUpdateModel {
    return {
      flavorId: this.selectedFlavor!.id,
      version: this.version,
      versionCode: this.versionCode ?? 0,
      downloadUrl: this.downloadUrl,
      updatePriority: this.selectedPriority!,
      deadline:
        this.selectedPriority === UpdatePriorityEnum.Deadline && this.deadline
          ? this.deadline.toISOString()
          : null,
      releaseNotes: this.releaseNotes,
      updateStatus: this.selectedStatus ?? UpdateStatusEnum.Pilot
    };
  }

  private tryExtractVersionFromFilename(filename: string): void {
    const versionMatch = filename.match(/(\d+\.\d+\.\d+)/);
    if (versionMatch) this.version = versionMatch[1];

    if (filename.endsWith('.apk')) {
      const codeMatch = filename.match(/(?<=\.)\d+(?=\.apk$)/);
      if (codeMatch) this.versionCode = Number(codeMatch[0]);
    }
  }

  async deactivateUpdate(element: any): Promise<void> {
    await this.adminService.deactivatePdvUpdate(element.id)
    await this.loadUpdates();
  }

  resetForm(): void {
    this.selectedFlavor = null;
    this.selectedPriority = null;
    this.selectedStatus = UpdateStatusEnum.Production;
    this.version = '';
    this.versionCode = null;
    this.releaseNotes = '';
    this.selectedFile = null;
    this.downloadUrl = null;
    this.deadline = new Date();
    this.selectedStatus = null;
  }
}